import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  createChecklist,
  updateChecklist,
  deleteChecklist,
  fetchChecklistsForCourse,
  fetchChecklistsForSession,
  createSessionChecklist,
  moveChecklist,
  fetchChecklistProgress,
  fetchRegistrationsForClass,
  fetchUsersByIds,
  fetchCourseSkillSheetAssignments,
  removeSkillSheetAssignment,
  updateSkillSheetAssignment,
  pushSkillSheetAssignmentToBank,
  reorderClassChecklists,
  reorderSessionChecklists,
} from "../../api/backend";
import SkillSheetBuilderModal from "../../components/SkillSheetBuilderModal";
import HowToGuideBox from "../../components/HowToGuideBox";
import AddFromBankModal from "../../components/AddFromBankModal";
import SaveToBankPromptDialog from "../../components/SaveToBankPromptDialog";
import { useTheme, alpha } from "@mui/material/styles";

import ConfirmDialog from "../../components/ConfirmDialog";
import { itemPoints, totalPoints } from "../../components/checklistScoring";
import Icon from "./skillChecklists/Icon";
import { moveMenuItems } from "../../components/moveMenu";
import ChecklistRow from "./skillChecklists/ChecklistRow";
import { buildBankModel, buildChecklistModel } from "./skillChecklists/checklistModel";
import { formatTime, parseDateInfo } from "./sessionsList/sessionsModel";
import "./skillChecklists/skillChecklists.css";
const T = {
  danger: "var(--color-danger)",
  danger50: "var(--color-danger-bg)",
  ink: "var(--color-ink)",
  ink2: "rgba(31,22,51,0.75)",
  ink3: "rgba(31,22,51,0.6)",
  ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
};

function getStatus(cl) {
  return cl.enabled !== false ? "published" : "draft";
}

const bankStatus = (a) => (a.enabled ? "published" : "draft");

const isDragged = (dragged, item, bank = false) =>
  Boolean(dragged) && dragged.id === item.id && Boolean(dragged._bank) === bank;

function printChecklist(cl) {
  const items = Array.isArray(cl.items) ? cl.items : [];
  const rows = items
    .map(
      (item, idx) => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;width:36px;color:var(--color-ink-3);">${idx + 1}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;">
        ${item.isCritical ? '<span style="color:var(--color-warn);margin-right:4px;">&#9651;</span>' : ""}
        ${item.criteria || ""}
      </td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;text-align:center;width:60px;">${itemPoints(item)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;text-align:center;width:80px;">______</td>
    </tr>`,
    )
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>${cl.title || "Skill Sheet"}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .meta { font-size: 12px; color: #555; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    thead tr { background: var(--color-surface-2); }
    th { padding: 6px 8px; text-align: left; font-size: 11px; text-transform: uppercase; color: var(--color-ink-2); font-weight: 700; }
    .info-row { display: flex; gap: 24px; margin-bottom: 24px; flex-wrap: wrap; }
    .info-field { flex: 1 1 180px; }
    .info-field label { font-size: 10px; text-transform: uppercase; color: var(--color-ink-3); font-weight: 700; display: block; margin-bottom: 2px; }
    .info-field .line { border-bottom: 1px solid var(--color-ink-2); height: 20px; }
    .signature-row { display: flex; gap: 24px; margin-top: 32px; }
    .sig-field { flex: 1 1 200px; }
    .sig-field label { font-size: 10px; text-transform: uppercase; color: var(--color-ink-3); font-weight: 700; display: block; margin-bottom: 2px; }
    .sig-field .line { border-bottom: 1px solid var(--color-ink-2); height: 32px; }
    @media print { body { margin: 12px; } }
  </style>
</head>
<body>
  <h1>${cl.title || "Skill Sheet"}</h1>
  <p class="meta">${cl.description || ""} ${cl.passingScore != null ? `&nbsp;|&nbsp; Passing Score: ${cl.passingScore} / ${totalPoints(items)} pts` : ""}</p>
  <div class="info-row">
    <div class="info-field"><label>Candidate Name</label><div class="line"></div></div>
    <div class="info-field"><label>Examiner Name</label><div class="line"></div></div>
    <div class="info-field"><label>Date</label><div class="line"></div></div>
    <div class="info-field"><label>Location</label><div class="line"></div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Performance Criteria</th>
        <th style="text-align:center">Pts</th>
        <th style="text-align:center">Score</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <tr style="background:var(--color-surface-2);">
        <td colspan="2" style="padding:6px 8px;font-weight:700;font-size:13px;">Total</td>
        <td style="padding:6px 8px;text-align:center;font-weight:700;font-size:13px;">${totalPoints(items)}</td>
        <td style="padding:6px 8px;text-align:center;font-weight:700;font-size:13px;">____</td>
      </tr>
    </tbody>
  </table>
  <div class="signature-row">
    <div class="sig-field"><label>Candidate Signature</label><div class="line"></div></div>
    <div class="sig-field"><label>Examiner Signature</label><div class="line"></div></div>
    <div class="sig-field"><label>Start Time</label><div class="line"></div></div>
    <div class="sig-field"><label>End Time</label><div class="line"></div></div>
  </div>
</body>
</html>`;

  const win = window.open("", "_blank", "width=800,height=900");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

function ChecklistPreviewDialog({ open, onClose, cl, onPrint }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  const tSecondary50 = alpha(tSecondary, 0.1);
  if (!cl) return null;
  const items = Array.isArray(cl.items) ? cl.items : [];
  const maxPoints = items.length ? totalPoints(items) : cl.itemCount || 0;
  const passingPct =
    maxPoints > 0 && cl.passingScore != null
      ? Math.round((cl.passingScore / maxPoints) * 100)
      : null;

  const infoFields = ["Candidate Name", "Examiner Name", "Date", "Location"];
  const sigFields = [
    "Candidate Signature",
    "Examiner Signature",
    "Start Time",
    "End Time",
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "14px", maxHeight: "90vh" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              mb: "4px",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color={T.ink}
              sx={{ fontFamily: "var(--font-display)" }}
            >
              {cl.title || "Untitled Checklist"}
            </Typography>
            {cl.passingScore != null && (
              <Chip
                label={`Pass: ${cl.passingScore} / ${maxPoints} pts${passingPct != null ? ` (${passingPct}%)` : ""}`}
                size="small"
                sx={{
                  bgcolor: tSecondary50,
                  color: tSecondary,
                  fontWeight: 600,
                  fontSize: 11,
                  border: `1px solid ${alpha(tSecondary, 0.2)}`,
                }}
              />
            )}
          </Box>
          {cl.description && (
            <Typography sx={{ fontSize: 13, color: T.ink3 }}>
              {cl.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <Tooltip title="Print blank">
            <IconButton size="small" onClick={onPrint} sx={{ color: T.ink3 }}>
              <PrintIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: T.ink3 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Info fields row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
            gap: "12px",
            mb: 3,
          }}
        >
          {infoFields.map((label) => (
            <Box key={label}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  color: T.ink3,
                  mb: "4px",
                }}
              >
                {label}
              </Typography>
              <Box sx={{ borderBottom: `1.5px solid ${T.ink2}`, height: 24 }} />
            </Box>
          ))}
        </Box>

        {/* Criteria table */}
        {items.length > 0 ? (
          <Box
            sx={{
              border: `1px solid ${T.line}`,
              borderRadius: "10px",
              overflow: "hidden",
              mb: 3,
            }}
          >
            {/* Table header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "36px 1fr 60px 80px",
                bgcolor: T.surface2,
                borderBottom: `1px solid ${T.line}`,
                px: "12px",
                py: "8px",
              }}
            >
              {["#", "Performance Criteria", "Pts", "Score"].map((h, i) => (
                <Typography
                  key={h}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    color: T.ink3,
                    textAlign: i >= 2 ? "center" : "left",
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {/* Rows */}
            {items.map((item, idx) => (
              <Box
                key={item.id ?? idx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "36px 1fr 60px 80px",
                  px: "12px",
                  py: "10px",
                  borderBottom:
                    idx < items.length - 1 ? `1px solid ${T.line2}` : "none",
                  alignItems: "flex-start",
                  bgcolor:
                    idx % 2 === 0
                      ? "var(--color-surface-canvas-light)"
                      : T.surface2,
                }}
              >
                <Typography sx={{ fontSize: 12, color: T.ink4, pt: "1px" }}>
                  {idx + 1}
                </Typography>
                <Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    {item.isCritical && (
                      <Tooltip title="Critical step">
                        <WarningAmberIcon
                          sx={{
                            fontSize: 13,
                            color: "var(--color-warn)",
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    )}
                    <Typography
                      sx={{ fontSize: 13, color: T.ink, lineHeight: 1.4 }}
                    >
                      {item.criteria ||
                        item.title ||
                        item.description ||
                        `Step ${idx + 1}`}
                    </Typography>
                  </Box>
                  {item.notes && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: T.ink3,
                        mt: "2px",
                        fontStyle: "italic",
                      }}
                    >
                      {item.notes}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{ fontSize: 12, color: T.ink2, textAlign: "center" }}
                >
                  {itemPoints(item)}
                </Typography>
                <Box
                  sx={{
                    borderBottom: `1px solid ${T.ink3}`,
                    height: 20,
                    mx: "auto",
                    width: 48,
                  }}
                />
              </Box>
            ))}

            {/* Total row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "36px 1fr 60px 80px",
                px: "12px",
                py: "8px",
                bgcolor: T.surface2,
                borderTop: `1px solid ${T.line}`,
              }}
            >
              <Box />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
                Total
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: T.ink,
                  textAlign: "center",
                }}
              >
                {maxPoints}
              </Typography>
              <Box
                sx={{
                  borderBottom: `1px solid ${T.ink3}`,
                  height: 20,
                  mx: "auto",
                  width: 48,
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              border: `1.5px dashed ${T.line}`,
              borderRadius: "10px",
              p: "32px",
              textAlign: "center",
              bgcolor: T.surface2,
              mb: 3,
            }}
          >
            <Typography sx={{ fontSize: 13, color: T.ink3 }}>
              No criteria items added yet.
            </Typography>
          </Box>
        )}

        {/* Signature row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
            gap: "12px",
            mt: 1,
          }}
        >
          {sigFields.map((label) => (
            <Box key={label}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  color: T.ink3,
                  mb: "6px",
                }}
              >
                {label}
              </Typography>
              <Box sx={{ borderBottom: `1.5px solid ${T.ink2}`, height: 28 }} />
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/** Drag-and-drop handlers shared by checklist and bank rows; `active` is whether this row accepts a drop. */
function rowDragProps({ draggable, onDragStart, onDragEnd, active, onDrop, setDropOver }) {
  return {
    draggable: Boolean(draggable),
    onDragStart,
    onDragEnd,
    onDragOver: (e) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      setDropOver(true);
    },
    onDragLeave: () => setDropOver(false),
    onDrop: (e) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      setDropOver(false);
      onDrop?.();
    },
  };
}

function ChecklistCard({
  cl,
  sessions,
  onEdit,
  onDelete,
  onMove,
  onTogglePublish,
  canEdit,
  contentLocked = false,
  courseId,
  progressData,
  studentMap,
  publishingId,
  draggable,
  onDragStart,
  onDragEnd,
  dragging,
  canDropHere,
  onDropBefore,
  showReorder,
  onMoveUp,
  onMoveDown,
  moveUpDisabled,
  moveDownDisabled,
}) {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [dropOver, setDropOver] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const progressStudents = progressData?.students;
  const model = React.useMemo(
    () =>
      buildChecklistModel(cl, {
        progressStudents: progressStudents || [],
        enrolled: Object.keys(studentMap).length,
        studentMap,
      }),
    [cl, progressStudents, studentMap],
  );

  const sessionLocked = cl.sessionId
    ? Boolean(sessions?.find((s) => s.id === cl.sessionId)?.locked)
    : false;
  // toggleDisabled governs the publish switch (canEdit and an unlocked session). contentDisabled also covers the
  // content controls (edit, move, delete), which stay off when the class content is locked.
  const toggleDisabled = !canEdit || sessionLocked;
  const contentDisabled = toggleDisabled || contentLocked;
  const moveLocked = contentDisabled || Boolean(cl.inLearningPlan);
  const resultsPath = `/course-management/${courseId}/checklists/${cl.id}/assess`;

  const moveIcon = (kind) => <Icon n={kind === "class" ? "layers" : "cal"} s={15} />;
  const publishItems = toggleDisabled
    ? []
    : [
        {
          icon: <Icon n={model.published ? "eyeOff" : "eye"} s={15} />,
          label: model.published ? "Unpublish" : "Publish",
          onSelect: () => onTogglePublish(cl),
        },
      ];
  const deleteItems = contentDisabled
    ? []
    : [{ icon: <Icon n="trash" s={15} />, label: "Delete", danger: true, onSelect: () => onDelete(cl) }];
  const menuItems = [
    { icon: <Icon n="eye" s={15} />, label: "Preview", onSelect: () => setPreviewOpen(true) },
    { icon: <Icon n="print" s={15} />, label: "Print blank", onSelect: () => printChecklist(cl) },
    ...(contentDisabled ? [] : [{ icon: <Icon n="pencil" s={15} />, label: "Edit checklist", onSelect: () => onEdit(cl) }]),
    { icon: <Icon n="chart" s={15} />, label: "Completion report", onSelect: () => navigate(resultsPath) },
    ...moveMenuItems({ item: cl, sessions, onMove, locked: moveLocked, classLabel: "Class-level checklists", icon: moveIcon }),
    ...(publishItems.length || deleteItems.length ? [{ separator: true }] : []),
    ...publishItems,
    ...deleteItems,
  ];

  return (
    <>
      <ChecklistRow
        model={model}
        canEdit={canEdit}
        expanded={expanded}
        onExpand={() => setExpanded((e) => !e)}
        publishing={publishingId === cl.id}
        onTogglePublish={() => canEdit && onTogglePublish(cl)}
        onAssess={() => navigate(resultsPath)}
        onReport={() => navigate(resultsPath)}
        onConfigure={contentLocked ? undefined : () => onEdit(cl)}
        menuItems={menuItems}
        dragging={dragging}
        dropOver={dropOver}
        dragProps={rowDragProps({
          draggable: draggable && !moveLocked,
          onDragStart: () => onDragStart?.(cl),
          onDragEnd: () => onDragEnd?.(),
          active: canDropHere && !moveLocked,
          onDrop: onDropBefore,
          setDropOver,
        })}
        reorder={
          showReorder
            ? {
                upDisabled: moveUpDisabled || moveLocked,
                downDisabled: moveDownDisabled || moveLocked,
                onUp: onMoveUp,
                onDown: onMoveDown,
              }
            : null
        }
      />
      <ChecklistPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        cl={cl}
        onPrint={() => printChecklist(cl)}
      />
    </>
  );
}

function SessionRow({
  session,
  checklists,
  loading,
  canEdit,
  contentLocked = false,
  onEdit,
  onDelete,
  onMove,
  onTogglePublish,
  courseId,
  progressData,
  studentMap,
  publishingId,
  sessions,
  onAddNew,
  draggedItem,
  onDragStartItem,
  onDragEndItem,
  onDropTarget,
  onDropOnItem,
  bankAssignments,
  bankCardProps,
}) {
  const [dragOver, setDragOver] = React.useState(false);
  const locked = Boolean(session.locked);
  const editable = canEdit && !contentLocked;
  const canDrop = editable && !locked && draggedItem;
  const info = parseDateInfo(session.sessionDate);
  const bank = bankAssignments || [];
  const sorted = Array.isArray(checklists)
    ? checklists.slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    : [];
  const count = sorted.length + bank.length;

  return (
    <div
      className={`grpbox${dragOver ? " drop" : ""}`}
      data-testid={`session-group-${session.id}`}
      onDragOver={(e) => {
        if (canDrop) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (canDrop) onDropTarget?.(session.id);
      }}
    >
      <div className="grp">
        <b>{session.sessionNumber || `Session ${session.id}`}</b>
        {info && <span>{info.weekday}, {info.month} {info.day}</span>}
        {session.startTime && <span>{formatTime(session.startTime)}</span>}
        {locked && <span className="lock"><Icon n="lock" s={12} />Locked</span>}
      </div>
      {loading || !Array.isArray(checklists) ? (
        <div className="none">Loading checklists…</div>
      ) : count === 0 ? (
        <div className="none">
          No checklists attached
          {editable && !locked && (
            <button type="button" className="lnk" onClick={() => onAddNew(session)}>
              <Icon n="plus" />Attach checklist
            </button>
          )}
        </div>
      ) : (
        <>
          {sorted.map((cl, idx) => (
            <ChecklistCard
                key={cl.id}
                cl={cl}
                sessions={sessions}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
                onTogglePublish={onTogglePublish}
                canEdit={canEdit && !locked}
                contentLocked={contentLocked}
                courseId={courseId}
                progressData={progressData}
                studentMap={studentMap}
                publishingId={publishingId}
                draggable={editable && !locked}
                onDragStart={onDragStartItem}
                onDragEnd={onDragEndItem}
                dragging={isDragged(draggedItem, cl)}
                canDropHere={editable && !locked && draggedItem && !isDragged(draggedItem, cl)}
                onDropBefore={() => onDropOnItem?.(cl.id)}
                showReorder={editable && !locked && sorted.length > 1}
                moveUpDisabled={idx === 0}
                moveDownDisabled={idx === sorted.length - 1}
                onMoveUp={() => onDropOnItem?.(sorted[idx - 1]?.id ?? null, cl)}
                onMoveDown={() => onDropOnItem?.(sorted[idx + 2]?.id ?? null, cl)}
              />
          ))}
          {bank.map((a) => (
            <BankAssignmentCard
                key={`bank-${a.id}`}
                assignment={a}
                {...bankCardProps}
                canEdit={canEdit && !locked}
                contentLocked={contentLocked}
                draggable={editable && !locked}
                dragging={isDragged(draggedItem, a, true)}
              />
          ))}
          {editable && !locked && (
            <div className="none">
              <button type="button" className="lnk" onClick={() => onAddNew(session)}>
                <Icon n="plus" />Attach checklist
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BankAssignmentCard({
  assignment,
  courseId,
  onRemove,
  onToggle,
  onSaved,
  publishingId,
  canEdit,
  contentLocked = false,
  sessions,
  onMove,
  draggable,
  onDragStart,
  onDragEnd,
  dragging,
}) {
  const sessionLocked = assignment.sessionId
    ? Boolean(sessions?.find((s) => s.id === assignment.sessionId)?.locked)
    : false;
  // editable governs content controls (edit, remove, move); canEdit alone governs the publish switch.
  const editable = canEdit && !contentLocked;
  const moveLocked = !editable || sessionLocked || Boolean(assignment.inLearningPlan);
  const model = React.useMemo(() => buildBankModel(assignment), [assignment]);
  const [expanded, setExpanded] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [editTitle, setEditTitle] = React.useState(model.title);
  const [editDescription, setEditDescription] = React.useState(model.description);
  const [editPeerRequired, setEditPeerRequired] = React.useState(assignment.sheet?.peerAssessmentRequired !== false);
  const [promptOpen, setPromptOpen] = React.useState(false);

  const handleStartEdit = () => {
    setEditTitle(model.title);
    setEditDescription(model.description);
    setEditPeerRequired(assignment.sheet?.peerAssessmentRequired !== false);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    const updated = await updateSkillSheetAssignment(courseId, assignment.id, {
      title: editTitle,
      description: editDescription,
      peerAssessmentRequired: editPeerRequired,
    });
    onSaved(updated);
    setEditing(false);
    if (assignment.skillSheetId) setPromptOpen(true);
  };

  const handlePromptChoice = async (mode) => {
    setPromptOpen(false);
    if (mode === "KEEP_LOCAL") return;
    const updated = await pushSkillSheetAssignmentToBank(courseId, assignment.id, mode);
    onSaved(updated);
  };

  const publishItems = canEdit
    ? [
        {
          icon: <Icon n={model.published ? "eyeOff" : "eye"} s={15} />,
          label: model.published ? "Unpublish" : "Publish",
          onSelect: () => onToggle(assignment),
        },
      ]
    : [];
  const removeItems = editable
    ? [{ icon: <Icon n="trash" s={15} />, label: "Remove from class", danger: true, onSelect: () => onRemove(assignment) }]
    : [];
  const menuItems = [
    ...(editable ? [{ icon: <Icon n="pencil" s={15} />, label: "Edit", onSelect: handleStartEdit }] : []),
    ...moveMenuItems({ item: assignment, sessions, onMove, locked: moveLocked, classLabel: "Class-level checklists", icon: (kind) => <Icon n={kind === "class" ? "layers" : "cal"} s={15} /> }),
    ...(publishItems.length || removeItems.length ? [{ separator: true }] : []),
    ...publishItems,
    ...removeItems,
  ];

  const titleSlot = editing ? (
    <div className="edit">
      <input aria-label="Skill sheet title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
      <input aria-label="Skill sheet description" placeholder="Description" value={editDescription}
        onChange={(e) => setEditDescription(e.target.value)} />
      <label>
        <input type="checkbox" checked={editPeerRequired} onChange={(e) => setEditPeerRequired(e.target.checked)} />
        Require peer assessment before instructor sign-off
      </label>
    </div>
  ) : null;

  return (
    <>
      <ChecklistRow
        model={model}
        canEdit={canEdit}
        expanded={expanded}
        onExpand={() => setExpanded((e) => !e)}
        publishing={publishingId === assignment.id}
        onTogglePublish={() => canEdit && onToggle(assignment)}
        onConfigure={editable ? handleStartEdit : undefined}
        menuItems={menuItems}
        titleSlot={titleSlot}
        extraAction={editing ? <button type="button" className="btn btn-p" onClick={handleSaveEdit}>Save</button> : null}
        dragging={dragging}
        dragProps={{
          draggable: Boolean(draggable) && !moveLocked,
          onDragStart: () => onDragStart?.({ ...assignment, _bank: true }),
          onDragEnd: () => onDragEnd?.(),
        }}
      />
      <SaveToBankPromptDialog open={promptOpen} sourceLabel="skill sheet" onClose={() => setPromptOpen(false)} onChoose={handlePromptChoice} />
    </>
  );
}

function Toolbar({
  query,
  setQuery,
  filterStatus,
  setFilterStatus,
  counts,
  onNew,
  onFromBank,
  canEdit,
}) {
  const filters = [
    { key: "all", label: "All" },
    { key: "published", label: "Published" },
    { key: "draft", label: "Not published" },
  ];
  return (
    <div className="bar-t">
      <label className="search">
        <Icon n="search" s={15} />
        <input
          type="search"
          placeholder="Search checklists"
          aria-label="Search checklists"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <div className="seg" role="group" aria-label="Filter checklists">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            className={filterStatus === f.key ? "on" : ""}
            aria-pressed={filterStatus === f.key}
            onClick={() => setFilterStatus(f.key)}
          >
            {f.label} <b>{counts[f.key] ?? 0}</b>
          </button>
        ))}
      </div>
      <span className="sp" />
      <HowToGuideBox
          iconOnly
          title="How to Use Skill Check Sheets"
          role="Instructors & Course Coordinators"
          steps={[
            {
              number: 1,
              title: "Create a Skill Check Sheet",
              description:
                "Click 'New Checklist' to build a skill assessment sheet.",
              details:
                "Each checklist contains a list of skills students must demonstrate.",
            },
            {
              number: 2,
              title: "Add Skills and Criteria",
              description:
                "Use the builder to add individual skill items with pass/fail criteria.",
              details: [
                "Each skill item can have a title and description",
                "Mark items as required or optional",
              ],
            },
            {
              number: 3,
              title: "Assign to a Session",
              description:
                "Attach the checklist to a session so it appears for that class day.",
              details:
                "Students and peers can complete their portion once it's assigned.",
            },
            {
              number: 4,
              title: "Students Complete Peer Check",
              description:
                "Students verify each other's skills during training.",
              details:
                "The peer section must be fully completed before requesting instructor sign-off.",
            },
            {
              number: 5,
              title: "Instructor Signs Off",
              description:
                "Review the peer-completed checklist and provide your final sign-off.",
              details: [
                "Only instructors can complete the sign-off section",
                "Sign-off is recorded with your name and timestamp",
              ],
            },
          ]}
          tips={[
            "Be specific with skill descriptions so students know exactly what's being evaluated.",
            "Assign checklists to sessions before class day so students can review what's expected.",
            "Use the progress view to see which students still need sign-offs.",
            "Print or export completed checklists for certification records.",
          ]}
        />
      {canEdit && onFromBank && (
        <button type="button" className="btn btn-o" onClick={onFromBank}>
          <Icon n="plus" />From Bank
        </button>
      )}
      {canEdit && (
        <button type="button" className="btn btn-p" onClick={onNew}>
          <Icon n="plus" />New Checklist
        </button>
      )}
    </div>
  );
}


export default function SkillChecklistsTab({
  checklists,
  setChecklists,
  sessions,
  canEdit,
  contentLocked = false,
}) {
  const { id: courseId } = useParams();
  // Content edits (create, edit, delete, move, reorder, remove) need canEdit and an unlocked class. The publish
  // switch still uses canEdit alone.
  const editContent = canEdit && !contentLocked;

  const [searchQuery, setSearchQuery] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("all");

  const [sessionChecklists, setSessionChecklists] = React.useState({});
  const [loadingSessionIds, setLoadingSessionIds] = React.useState({});

  const [progressData, setProgressData] = React.useState(null);
  const [studentMap, setStudentMap] = React.useState({});

  const [publishingId, setPublishingId] = React.useState(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogTarget, setDialogTarget] = React.useState("class");
  const [editTarget, setEditTarget] = React.useState(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [removeAssignment, setRemoveAssignment] = React.useState(null);
  const [bankPrompt, setBankPrompt] = React.useState(null); // { checklist, warning }
  const [passWarn, setPassWarn] = React.useState(null); // { passed, resolve, reject }
  const [bankPushError, setBankPushError] = React.useState("");

  const [bankModalOpen, setBankModalOpen] = React.useState(false);
  const [bankAssignments, setBankAssignments] = React.useState([]);
  const [bankPublishingId, setBankPublishingId] = React.useState(null);

  const [draggedChecklist, setDraggedChecklist] = React.useState(null);
  const [classDropOver, setClassDropOver] = React.useState(false);

  React.useEffect(() => {
    fetchCourseSkillSheetAssignments(courseId)
      .then((d) => setBankAssignments(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [courseId]);

  React.useEffect(() => {
    const load = async () => {
      try {
        const [progress, regs] = await Promise.all([
          fetchChecklistProgress(courseId),
          fetchRegistrationsForClass(courseId),
        ]);
        setProgressData(progress);
        const ids = (regs || []).map((r) => r.userId).filter(Boolean);
        if (ids.length > 0) {
          const users = await fetchUsersByIds(ids);
          const map = {};
          (Array.isArray(users) ? users : []).forEach((u) => {
            map[u.id] = u;
          });
          setStudentMap(map);
        }
      } catch {
        // silent
      }
    };
    load();
  }, [courseId]);

  React.useEffect(() => {
    const sessionList = Array.isArray(sessions) ? sessions : [];
    if (sessionList.length === 0) return;

    const missingSessionIds = sessionList
      .map((s) => s.id)
      .filter((id) => sessionChecklists[id] === undefined);

    if (missingSessionIds.length === 0) return;

    let cancelled = false;

    setLoadingSessionIds((prev) => {
      const next = { ...prev };
      missingSessionIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    Promise.all(
      missingSessionIds.map(async (sessionId) => {
        try {
          const items = await fetchChecklistsForSession(courseId, sessionId);
          return [sessionId, Array.isArray(items) ? items : []];
        } catch {
          return [sessionId, []];
        }
      }),
    ).then((results) => {
      if (cancelled) return;

      setSessionChecklists((prev) => {
        const next = { ...prev };
        results.forEach(([sessionId, items]) => {
          next[sessionId] = items;
        });
        return next;
      });

      setLoadingSessionIds((prev) => {
        const next = { ...prev };
        missingSessionIds.forEach((id) => {
          next[id] = false;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [courseId, sessions, sessionChecklists]);

  const openAdd = (target) => {
    if (!editContent) return;
    setDialogTarget(target);
    setEditTarget(null);
    setDialogOpen(true);
  };

  const openEdit = (checklist) => {
    if (!editContent) return;
    setDialogTarget(
      checklist.sessionId != null ? { id: checklist.sessionId } : "class",
    );
    setEditTarget(checklist);
    setDialogOpen(true);
  };

  // Students with a locked PASS on this checklist (best-effort: 0 when progress can't be loaded).
  const countLockedPasses = async (checklistId) => {
    try {
      const progress = await fetchChecklistProgress(courseId);
      return (progress?.students || []).filter((st) =>
        (st.checklists || []).some((c) => c.checklistId === checklistId && c.status === "PASS")).length;
    } catch {
      return 0;
    }
  };

  const promptSaveToBank = async (updated) => {
    // canEdit is coordinator/admin only (the roles allowed to push to the bank).
    if (!editContent || !updated.sourceSkillSheetBankId) return;
    let warning = null;
    if (await countLockedPasses(updated.id) > 0) {
      warning = "A student already has a locked PASS on this skill sheet. Updating the original bank item will not change that result.";
    }
    setBankPrompt({ checklist: updated, warning });
  };

  const handleBankPromptChoice = async (mode) => {
    const { checklist } = bankPrompt;
    setBankPrompt(null);
    if (mode === "KEEP_LOCAL") return;
    try {
      await pushSkillSheetAssignmentToBank(courseId, checklist.id, mode);
    } catch (e) {
      setBankPushError(e?.response?.data?.message || "Could not save this change to the Knowledge Bank.");
    }
  };

  const handleSave = async (formData) => {
    // The builder sends back the id of a checklist it has already saved (auto-save), so this updates it, never a copy.
    const { autosave, savedBankId, requestedStatus, ...body } = formData;
    const targetId = editTarget?.id ?? savedBankId ?? null;
    if (targetId) {
      const passed = await countLockedPasses(targetId);
      if (passed > 0 && autosave) {
        // Auto-save never asks for confirmation; an explicit Save does.
        throw new Error("This skill sheet has passed evaluations. Press Save Changes to confirm your edits.");
      }
      // Cancel rejects, so the builder stays open with the message and nothing is saved.
      if (passed > 0) await new Promise((resolve, reject) => setPassWarn({ passed, resolve, reject }));
      const updated = await updateChecklist(targetId, body);
      if (!autosave) await promptSaveToBank(updated);
      if (updated.sessionId == null) {
        setChecklists((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c)),
        );
      } else {
        setSessionChecklists((prev) => ({
          ...prev,
          [updated.sessionId]: (prev[updated.sessionId] || []).map((c) =>
            c.id === updated.id ? updated : c,
          ),
        }));
      }
      return { id: updated.id };
    }
    if (dialogTarget === "class") {
      const created = await createChecklist(courseId, body);
      setChecklists((prev) => [...prev, created]);
      return { id: created.id };
    }
    const created = await createSessionChecklist(courseId, dialogTarget.id, body);
    setSessionChecklists((prev) => ({
      ...prev,
      [dialogTarget.id]: [...(prev[dialogTarget.id] || []), created],
    }));
    return { id: created.id };
  };

  const handleDelete = async (checklist) => {
    if (!editContent) return;
    await deleteChecklist(checklist.id);
    if (checklist.sessionId == null) {
      setChecklists((prev) => prev.filter((c) => c.id !== checklist.id));
    } else {
      setSessionChecklists((prev) => ({
        ...prev,
        [checklist.sessionId]: (prev[checklist.sessionId] || []).filter(
          (c) => c.id !== checklist.id,
        ),
      }));
    }
    setDeleteConfirm(null);
  };

  // scopeKey: null = class-level, otherwise a sessionId. Reorder + move share
  // this one code path (drag-and-drop, the up/down arrows, and the "Move
  // to..." menu all funnel through here), same convention as DocumentsTab.
  const getScopeChecklists = (scopeKey) =>
    scopeKey === null ? checklists || [] : sessionChecklists[scopeKey] || [];

  const setScopeChecklists = (scopeKey, updater) => {
    if (scopeKey === null) {
      setChecklists(updater);
    } else {
      setSessionChecklists((prev) => ({
        ...prev,
        [scopeKey]: updater(prev[scopeKey] || []),
      }));
    }
  };

  const handleDrop = async (item, targetScopeKey, targetItemId) => {
    if (!editContent || !item) return;
    const sourceScopeKey = item.sessionId ?? null;
    if (item._bank) {
      if (sourceScopeKey === targetScopeKey) return;
      const setScope = (scope) =>
        setBankAssignments((prev) =>
          prev.map((a) => (a.id === item.id ? { ...a, sessionId: scope } : a)),
        );
      setScope(targetScopeKey);
      try {
        await updateSkillSheetAssignment(courseId, item.id, {
          sessionId: targetScopeKey,
        });
      } catch {
        setScope(sourceScopeKey);
      }
      return;
    }
    const crossScope = sourceScopeKey !== targetScopeKey;

    const prevSource = getScopeChecklists(sourceScopeKey);
    const prevTarget = crossScope
      ? getScopeChecklists(targetScopeKey)
      : prevSource;

    const targetExisting = prevTarget
      .filter((c) => c.id !== item.id)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const insertAt =
      targetItemId == null
        ? targetExisting.length
        : Math.max(
            0,
            targetExisting.findIndex((c) => c.id === targetItemId),
          );

    const movedItem = { ...item, sessionId: targetScopeKey };
    const newOrderIds = [
      ...targetExisting.slice(0, insertAt).map((c) => c.id),
      item.id,
      ...targetExisting.slice(insertAt).map((c) => c.id),
    ];
    const newTargetList = newOrderIds.map((id, i) => {
      const found =
        id === item.id ? movedItem : targetExisting.find((c) => c.id === id);
      return { ...found, displayOrder: i };
    });

    if (crossScope) {
      setScopeChecklists(sourceScopeKey, (list) =>
        list.filter((c) => c.id !== item.id),
      );
    }
    setScopeChecklists(targetScopeKey, () => newTargetList);

    try {
      if (crossScope) {
        await moveChecklist(item.id, targetScopeKey);
      }
      if (targetScopeKey === null) {
        await reorderClassChecklists(courseId, newOrderIds);
      } else {
        await reorderSessionChecklists(courseId, targetScopeKey, newOrderIds);
      }
    } catch {
      setScopeChecklists(sourceScopeKey, () => prevSource);
      if (crossScope) setScopeChecklists(targetScopeKey, () => prevTarget);
    }
  };

  const makeDropHandler = (scopeKey) => (targetItemId, movingItem) =>
    handleDrop(movingItem || draggedChecklist, scopeKey, targetItemId ?? null);

  const handleTogglePublish = async (cl) => {
    if (publishingId) return;
    setPublishingId(cl.id);
    const wasEnabled = cl.enabled !== false;
    const optimisticUpdate = (list) =>
      list.map((c) => (c.id === cl.id ? { ...c, enabled: !wasEnabled } : c));
    if (cl.sessionId == null) {
      setChecklists(optimisticUpdate);
    } else {
      setSessionChecklists((prev) => ({
        ...prev,
        [cl.sessionId]: optimisticUpdate(prev[cl.sessionId] || []),
      }));
    }
    try {
      await updateChecklist(cl.id, { enabled: !wasEnabled });
    } catch {
      // Revert optimistic update on failure
      const revert = (list) =>
        list.map((c) => (c.id === cl.id ? { ...c, enabled: wasEnabled } : c));
      if (cl.sessionId == null) {
        setChecklists(revert);
      } else {
        setSessionChecklists((prev) => ({
          ...prev,
          [cl.sessionId]: revert(prev[cl.sessionId] || []),
        }));
      }
    } finally {
      setPublishingId(null);
    }
  };

  const handleBankTogglePublish = async (assignment) => {
    if (bankPublishingId) return;
    setBankPublishingId(assignment.id);
    const newEnabled = !assignment.enabled;
    setBankAssignments((prev) =>
      prev.map((a) =>
        a.id === assignment.id ? { ...a, enabled: newEnabled } : a,
      ),
    );
    try {
      await updateSkillSheetAssignment(courseId, assignment.id, {
        enabled: newEnabled,
      });
    } catch {
      setBankAssignments((prev) =>
        prev.map((a) =>
          a.id === assignment.id ? { ...a, enabled: assignment.enabled } : a,
        ),
      );
    } finally {
      setBankPublishingId(null);
    }
  };

  const applyFilter = (list) =>
    list
      .filter((cl) =>
        filterStatus === "all" ? true : getStatus(cl) === filterStatus,
      )
      .filter((cl) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          cl.title?.toLowerCase().includes(q) ||
          cl.description?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  const filteredChecklists = applyFilter(checklists);
  const bankForScope = (scopeKey) =>
    bankAssignments
      .filter((a) => (a.sessionId ?? null) === scopeKey)
      .filter((a) => filterStatus === "all" || bankStatus(a) === filterStatus)
      .filter((a) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          (a.sheet?.title || a.title || "").toLowerCase().includes(q) ||
          (a.sheet?.description || a.description || "").toLowerCase().includes(q)
        );
      });
  const classBank = bankForScope(null);
  const classTotal =
    checklists.length + bankAssignments.filter((a) => a.sessionId == null).length;
  const publishedCount =
    checklists.filter((c) => getStatus(c) === "published").length +
    bankAssignments.filter((a) => bankStatus(a) === "published").length;
  const draftCount =
    checklists.filter((c) => getStatus(c) === "draft").length +
    bankAssignments.filter((a) => bankStatus(a) === "draft").length;

  const counts = {
    all: checklists.length + bankAssignments.length,
    published: publishedCount,
    draft: draftCount,
  };

  const bankCardProps = {
    courseId,
    sessions,
    publishingId: bankPublishingId,
    onRemove: setRemoveAssignment,
    onToggle: handleBankTogglePublish,
    onSaved: (updated) =>
      setBankAssignments((prev) =>
        prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)),
      ),
    onMove: (item, targetSessionId) =>
      makeDropHandler(targetSessionId)(null, { ...item, _bank: true }),
    onDragStart: setDraggedChecklist,
    onDragEnd: () => setDraggedChecklist(null),
  };

  return (
    <Box className="sk-root">
      <Toolbar
        query={searchQuery}
        setQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        counts={counts}
        onNew={() => openAdd("class")}
        onFromBank={() => setBankModalOpen(true)}
        canEdit={editContent}
      />

      <AddFromBankModal
        open={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        courseId={courseId}
        type="skill-sheets"
        onAdded={() => {
          fetchChecklistsForCourse(courseId).then((d) =>
            setChecklists(Array.isArray(d) ? d : []),
          );
          fetchCourseSkillSheetAssignments(courseId).then((d) =>
            setBankAssignments(Array.isArray(d) ? d : []),
          );
        }}
      />

      {/* ── Class-level Checklists ── */}
      <section className="sec">
        <div className="sh">
          <div>
            <h2>Class-level Checklists</h2>
            <p>
              {filteredChecklists.length + classBank.length} of {classTotal} · applies to all sessions
            </p>
          </div>
        </div>

        {filteredChecklists.length === 0 && classBank.length === 0 ? (
          <div className="list">
            <div className="empty">
              {searchQuery || filterStatus !== "all" ? (
                <>
                  <b>No checklists match these filters</b>
                  Try a different status or clear the search.
                </>
              ) : (
                <>
                  <b>No class-level checklists yet</b>
                  {contentLocked
                    ? "This class uses a standardized course: its skill sheets cannot be changed."
                    : "Add a checklist to start tracking student skill completion."}
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`list${classDropOver ? " drop" : ""}`}
            onDragOver={(e) => {
              if (editContent && draggedChecklist) {
                e.preventDefault();
                setClassDropOver(true);
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget))
                setClassDropOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setClassDropOver(false);
              if (editContent && draggedChecklist) makeDropHandler(null)(null);
            }}
          >
            {filteredChecklists.map((cl, idx) => (
              <ChecklistCard
                key={cl.id}
                cl={cl}
                sessions={sessions}
                onEdit={openEdit}
                onDelete={setDeleteConfirm}
                onMove={(item, targetSessionId) =>
                  makeDropHandler(targetSessionId)(null, item)
                }
                onTogglePublish={handleTogglePublish}
                canEdit={canEdit}
                contentLocked={contentLocked}
                courseId={courseId}
                progressData={progressData}
                studentMap={studentMap}
                publishingId={publishingId}
                draggable={editContent}
                onDragStart={setDraggedChecklist}
                onDragEnd={() => setDraggedChecklist(null)}
                dragging={isDragged(draggedChecklist, cl)}
                canDropHere={
                  editContent && draggedChecklist && !isDragged(draggedChecklist, cl)
                }
                onDropBefore={() => makeDropHandler(null)(cl.id)}
                showReorder={editContent && filteredChecklists.length > 1}
                moveUpDisabled={idx === 0}
                moveDownDisabled={idx === filteredChecklists.length - 1}
                onMoveUp={() =>
                  makeDropHandler(null)(
                    filteredChecklists[idx - 1]?.id ?? null,
                    cl,
                  )
                }
                onMoveDown={() =>
                  makeDropHandler(null)(
                    filteredChecklists[idx + 2]?.id ?? null,
                    cl,
                  )
                }
              />
            ))}
            {classBank.map((a) => (
              <BankAssignmentCard
                key={`bank-${a.id}`}
                assignment={a}
                {...bankCardProps}
                canEdit={canEdit}
                contentLocked={contentLocked}
                draggable={editContent}
                dragging={isDragged(draggedChecklist, a, true)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Session Checklists ── */}
      <section className="sec">
        <div className="sh">
          <div>
            <h2>Session Checklists</h2>
            <p>
              Attach skill sheets to specific class sessions — drag to reorder, or
              onto another session to move
            </p>
          </div>
        </div>

        {(sessions || []).length === 0 ? (
          <div className="list">
            <div className="empty">No sessions found for this course.</div>
          </div>
        ) : (
          <div className="list">
            {(sessions || []).map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                checklists={sessionChecklists[s.id]}
                loading={Boolean(loadingSessionIds[s.id])}
                canEdit={canEdit}
                contentLocked={contentLocked}
                onEdit={openEdit}
                onDelete={setDeleteConfirm}
                onMove={(item, targetSessionId) =>
                  makeDropHandler(targetSessionId)(null, item)
                }
                onTogglePublish={handleTogglePublish}
                courseId={courseId}
                progressData={progressData}
                studentMap={studentMap}
                publishingId={publishingId}
                sessions={sessions}
                onAddNew={(session) => openAdd({ id: session.id })}
                draggedItem={draggedChecklist}
                onDragStartItem={setDraggedChecklist}
                onDragEndItem={() => setDraggedChecklist(null)}
                onDropTarget={() => makeDropHandler(s.id)(null)}
                onDropOnItem={makeDropHandler(s.id)}
                bankAssignments={bankForScope(s.id)}
                bankCardProps={bankCardProps}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Modal ── */}
      <SkillSheetBuilderModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initial={editTarget}
      />
      <SaveToBankPromptDialog
        open={!!bankPrompt}
        sourceLabel="skill sheet"
        warning={bankPrompt?.warning}
        onClose={() => setBankPrompt(null)}
        onChoose={handleBankPromptChoice}
      />
      <Snackbar open={!!bankPushError} autoHideDuration={6000} onClose={() => setBankPushError("")}>
        <Alert severity="error" onClose={() => setBankPushError("")}>{bankPushError}</Alert>
      </Snackbar>

      {/* ── Delete Confirm ── */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontFamily: "var(--font-display)" }}
        >
          Delete Checklist?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm)}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!passWarn}
        title="Edit a sheet students already passed?"
        message={`${passWarn?.passed} ${passWarn?.passed === 1 ? "student" : "students"} already passed this sheet; their results won't change.`}
        confirmLabel="Save changes"
        danger={false}
        onConfirm={() => passWarn.resolve()}
        onClose={() => {
          passWarn?.reject(new Error("Save cancelled. Nothing was changed."));
          setPassWarn(null);
        }}
      />

      <ConfirmDialog
        open={!!removeAssignment}
        title="Remove skill sheet from this class?"
        message={<>
          <b>“{removeAssignment?.sheet?.title || removeAssignment?.title}”</b> will be taken off this class, along with any
          changes made to it here. It stays in your Knowledge Bank.
        </>}
        confirmLabel="Remove"
        busyLabel="Removing…"
        onConfirm={async () => {
          await removeSkillSheetAssignment(courseId, removeAssignment.id);
          setBankAssignments((prev) => prev.filter((x) => x.id !== removeAssignment.id));
        }}
        onClose={() => setRemoveAssignment(null)}
      />
    </Box>
  );
}
